import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FileText, Mail, Loader2, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { invoiceApi } from '../../lib/api';
import { downloadBlob } from '../../lib/utils';

// PDF-Download (Rechnung) + E-Mail-Versand für In-House-Sammelrechnungen.
export function CollectiveInvoiceActions({ invoice, defaultEmail, onSent, onDeleted }) {
    const [busy, setBusy] = useState(null);
    const [open, setOpen] = useState(false);
    const [to, setTo] = useState('');

    const download = async () => {
        setBusy('invoice');
        try {
            const r = await invoiceApi.downloadPdf(invoice.id);
            downloadBlob(r.data, `Rechnung_${invoice.invoice_number}.pdf`);
        } catch { toast.error('PDF konnte nicht erstellt werden'); }
        finally { setBusy(null); }
    };

    const send = async () => {
        setBusy('mail');
        try {
            const r = await invoiceApi.sendEmail(invoice.id, to.trim());
            toast.success(`Rechnung ${invoice.invoice_number} an ${r.data.sent_to} gesendet`);
            setOpen(false);
            onSent && onSent();
        } catch (e) { toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen'); }
        finally { setBusy(null); }
    };

    const canDelete = invoice.status === 'open' && !(invoice.paid_amount > 0);
    const remove = async () => {
        if (!window.confirm(`Sammelrechnung ${invoice.invoice_number} wirklich löschen?\n\nDas PDF wird auch aus Dropbox entfernt. Danach kann für den Kurs eine neue Rechnung erstellt werden.`)) return;
        setBusy('delete');
        try {
            const r = await invoiceApi.deleteCollective(invoice.id);
            toast.success(`Sammelrechnung ${invoice.invoice_number} gelöscht`, {
                description: r.data.dropbox_deleted === false ? 'Achtung: Dropbox-PDF konnte nicht gelöscht werden' : (r.data.dropbox_deleted ? 'Dropbox-PDF entfernt' : undefined),
            });
            onDeleted && onDeleted();
        } catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
        finally { setBusy(null); }
    };

    return (
        <div className="flex gap-1 justify-end">
            <Button variant="outline" size="sm" onClick={download} disabled={busy === 'invoice'} title="Rechnung als PDF" data-testid={`collective-invoice-pdf-${invoice.id}`}>
                {busy === 'invoice' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1" />} PDF
            </Button>
            <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setTo(defaultEmail || invoice.sent_to || ''); }}>
                <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" title="Rechnung per E-Mail senden" data-testid={`collective-invoice-email-${invoice.id}`}>
                        <Mail className="h-3.5 w-3.5 mr-1" /> E-Mail
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 space-y-2" data-testid={`collective-invoice-email-popover-${invoice.id}`}>
                    <div className="text-sm font-semibold">Rechnung {invoice.invoice_number} senden</div>
                    <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="rechnung@firma.de" data-testid={`collective-invoice-email-to-${invoice.id}`} />
                    <p className="text-xs text-muted-foreground">Das Rechnungs-PDF wird als Anhang mitgeschickt.</p>
                    <Button size="sm" className="w-full" onClick={send} disabled={busy === 'mail' || !to.trim()} data-testid={`collective-invoice-email-send-${invoice.id}`}>
                        {busy === 'mail' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Senden
                    </Button>
                </PopoverContent>
            </Popover>
            {canDelete && (
                <Button variant="ghost" size="sm" onClick={remove} disabled={busy === 'delete'} title="Sammelrechnung löschen" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`collective-invoice-delete-${invoice.id}`}>
                    {busy === 'delete' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
            )}
        </div>
    );
}

export default CollectiveInvoiceActions;
